import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Printer, Receipt, StickyNote, MessageSquare } from 'lucide-react';
import PosV2SavTimeline from '../pos-v2/PosV2SavTimeline';
import MagasinBadge from '../../components/MagasinBadge';
import RepairStockPartsPanel from './RepairStockPartsPanel';
import RepairResteDisplay from './RepairResteDisplay';
import { fmtEuro, getAllowedStatuses, repairFinancials, repairStatusBadge } from './reparationsUtils';
import { printTicket80mm, downloadAvoirTicketPdf } from '../../utils/ticket80mmPrint';

const ReparationsDetailPanel = ({
  device,
  api,
  statuses,
  isAdmin,
  onStatusClick,
  onOpenPrint,
  onNotes,
  onChat,
  onCreateDevis,
  onAddAcompte,
  onDelete,
}) => {
  const [fullDevice, setFullDevice] = useState(null);
  const [avoirs, setAvoirs] = useState([]);

  useEffect(() => {
    if (!device?.ref) {
      setFullDevice(null);
      return undefined;
    }
    setFullDevice(device);
    setAvoirs([]);
    if (!api) return undefined;
    let cancelled = false;
    api.get(`/devices/${device.ref}`)
      .then((res) => { if (!cancelled) setFullDevice(res.data); })
      .catch(() => { if (!cancelled) setFullDevice(device); });
    api.get('/avoirs', { params: { device_ref: device.ref, limit: 20 } })
      .then((res) => { if (!cancelled) setAvoirs(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setAvoirs([]); });
    return () => { cancelled = true; };
  }, [device?.ref, api]);

  const handleRepairLinesUpdated = (data) => {
    if (!data || !device?.ref) return;
    setFullDevice((prev) => ({
      ...(prev || device),
      repair_lines: data.lines || prev?.repair_lines || [],
      pricing_mode: data.pricing_mode ?? prev?.pricing_mode,
      quoted_total: data.quoted_total ?? prev?.quoted_total,
      price_repair: data.price_repair ?? prev?.price_repair,
      price_delta: data.price_delta ?? prev?.price_delta,
      reste_a_payer: data.reste_a_payer ?? prev?.reste_a_payer,
      total_acomptes: data.total_acomptes ?? prev?.total_acomptes,
      remise: data.remise ?? prev?.remise,
    }));
    if (api) {
      api.get(`/devices/${device.ref}`).then((res) => setFullDevice(res.data)).catch(() => {});
    }
  };

  if (!device) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 text-sm p-8 text-center">
        Sélectionnez une réparation dans la liste
      </div>
    );
  }

  const view = fullDevice ?? device ?? {};
  const tone = repairStatusBadge(view?.status);
  const { total, acompte, reste } = repairFinancials(view);
  const allowed = getAllowedStatuses(view?.status, statuses || [], isAdmin);
  const received = view.created_at
    ? new Date(view.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-red-400">{view.ref}</span>
              {view.magasin_id && <MagasinBadge code={view.magasin_id} size="md" />}
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${tone.className}`}>
                {tone.label}
              </span>
            </div>
            <p className="text-base font-semibold text-white mt-2">{view.client_name || '—'}</p>
            <p className="text-sm text-gray-400 mt-0.5">
              {view.client_phone && (
                <a href={`tel:${view.client_phone}`} className="hover:text-white">{view.client_phone}</a>
              )}
              {view.client_phone && ' · '}
              reçu {received}
            </p>
          </div>
          <Link
            to={`/reparations/${view.ref}`}
            className="text-[10px] text-gray-400 hover:text-white flex items-center gap-1 shrink-0"
          >
            Fiche complète <ExternalLink size={12} />
          </Link>
        </div>
        <p className="text-sm font-medium text-gray-200">{view.model}</p>
        {view.problem && <p className="text-sm text-gray-500 mt-1">{view.problem}</p>}
        {view.technician_name && (
          <p className="text-xs text-gray-500 mt-2">Technicien · {view.technician_name}</p>
        )}
      </div>

      {api && (
        <RepairStockPartsPanel
          api={api}
          deviceRef={view.ref}
          magasinId={view.magasin_id}
          lines={view.repair_lines || []}
          pricingMode={view.pricing_mode}
          quotedTotal={view.quoted_total}
          priceRepair={view.price_repair}
          priceDelta={view.price_delta}
          remise={view.remise}
          clientEmail={view.client_email}
          onUpdated={handleRepairLinesUpdated}
        />
      )}

      <PosV2SavTimeline device={view} />

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
          <div className="text-[10px] text-gray-500 uppercase">Total</div>
          <div className="text-base font-bold text-white tabular-nums">{fmtEuro(total)} €</div>
        </div>
        <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
          <div className="text-[10px] text-gray-500 uppercase">Acompte</div>
          <button
            type="button"
            onClick={() => onAddAcompte?.(device)}
            className="text-base font-bold text-emerald-400 tabular-nums hover:text-emerald-300"
          >
            {fmtEuro(acompte)} €
          </button>
        </div>
        <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
          <div className="text-[10px] text-gray-500 uppercase">Reste</div>
          <div className={`text-base font-bold tabular-nums ${reste > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            <RepairResteDisplay
              reste={reste}
              avoirs={avoirs}
              onPrintAvoir={(refAvoir) => printTicket80mm(api, { type: 'avoir', ref: refAvoir }).catch((err) => alert(err.response?.data?.detail || err.message))}
              onDownloadAvoirPdf={(refAvoir) => downloadAvoirTicketPdf(api, refAvoir).catch((err) => alert(err.response?.data?.detail || err.message))}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!allowed.length}
          onClick={() => allowed.length && onStatusClick?.(view)}
          className="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-500 font-semibold text-sm disabled:opacity-40"
        >
          Changer statut
        </button>
        {reste > 0 && (
          <Link
            to="/pos-v2"
            className="min-h-[44px] px-4 rounded-lg bg-white/10 hover:bg-white/15 font-semibold text-sm flex items-center"
          >
            Encaisser reste
          </Link>
        )}
        <Link
          to="/pos-v2"
          className="min-h-[44px] px-4 rounded-lg border border-white/15 hover:bg-white/5 font-semibold text-sm flex items-center text-gray-300"
        >
          Ouvrir POS V2
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
        <button
          type="button"
          onClick={() => onOpenPrint?.(view.ref, 'ticket')}
          className="min-h-[36px] px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-300 flex items-center gap-1.5 hover:bg-white/15"
          data-testid={`print-repair-btn-${view.ref}`}
        >
          <Printer size={14} /> Imprimer
        </button>
        <button type="button" onClick={() => onNotes?.(view.ref)} className="min-h-[36px] px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-300 flex items-center gap-1.5 hover:bg-white/15">
          <StickyNote size={14} /> Notes
        </button>
        <button type="button" onClick={() => onChat?.(view)} className="min-h-[36px] px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-300 flex items-center gap-1.5 hover:bg-white/15">
          <MessageSquare size={14} /> Chat
        </button>
        {!view.linked_devis_id && (
          <button type="button" onClick={() => onCreateDevis?.(view)} className="min-h-[36px] px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-300 flex items-center gap-1.5 hover:bg-white/15">
            <Receipt size={14} /> Devis
          </button>
        )}
        {isAdmin && (
          <>
            <Link to={`/reparations/modifier/${view.ref}`} className="min-h-[36px] px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-300 flex items-center hover:bg-white/15">
              Modifier
            </Link>
            <button type="button" onClick={() => onDelete?.(view.ref)} className="min-h-[36px] px-3 rounded-lg bg-red-950/40 text-xs font-semibold text-red-300 hover:bg-red-950/60">
              Supprimer
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ReparationsDetailPanel;
